// The ∑: Variants / Sales History source list and its smart default
// (FIG-345 · UC-229), with no imports so the edge functions can use the exact
// same rules (compute-merchant-gmv-v1, UC-230). The platform checks are passed
// in from connectionPlatform (src/shared re-exports the edge module).

export interface ReportSourceOption {
  id: string;
  name: string;
  type: string;
}

/** 'saved' = the merchant's setting, 'default' = nothing saved, 'fallback' = saved source no longer connected. */
export type ResolvedOrigin = 'saved' | 'default' | 'fallback';

/** The shape of a saved choice (src/shared/types/reportDataSources.ts ReportSourceChoice). */
type ReportSourceChoiceLike =
  | { kind: 'all' }
  | { kind: 'source'; id: string }
  | { kind: 'sources'; ids: string[] };

/** A raw organisations.connections entry, as far as these rules read it. */
export interface ReportSourceConnection {
  id?: string | null;
  connectionId?: string | null;
  name?: string | null;
  type?: string | null;
  service?: string | null;
  connector_name?: string | null;
  import_type?: string | null;
  ecommerce_subtype?: string | null;
}

export interface ReportSourcePlatformHelpers {
  isReportsSelectableConnection: (connection: ReportSourceConnection) => boolean;
  detectEcommercePlatform: (connection: ReportSourceConnection) => string | null;
  getCsvEcommerceSubtype: (connection: ReportSourceConnection) => 'shopify' | 'amazon' | null;
}

type ConnectionLike = ReportSourceConnection;

/**
 * The report's source list: one option per reportable connection, an "All"
 * entry when there are several, or a single 'none' entry when there are none.
 */
export function buildReportSourceOptionsWith(helpers: ReportSourcePlatformHelpers, connections: unknown): ReportSourceOption[] {
  const sources: ReportSourceOption[] = [];
  const list = Array.isArray(connections) ? (connections as ConnectionLike[]) : [];

  if (list.length > 0) {
    const supportedConnections = list
      // Broken/error connections are kept: their synced data is still queryable.
      // Veeqo is excluded (inventory overlay only); see isReportsSelectableConnection.
      .filter((connection) => helpers.isReportsSelectableConnection(connection))
      .map((connection) => {
        const platform = helpers.detectEcommercePlatform(connection);
        const csvSubtype = helpers.getCsvEcommerceSubtype(connection);
        let connectionType = '';
        let isCsvImport = false;

        if (platform === 'shopify') {
          connectionType = 'Shopify';
        } else if (platform === 'amazon') {
          connectionType = 'Amazon';
        } else if (csvSubtype) {
          isCsvImport = true;
          connectionType = csvSubtype === 'shopify' ? 'Shopify' : 'Amazon';
        }

        if (!connectionType) return null;

        const connectionId = String(connection.id || connection.connectionId || '');
        let displayName = connection.name || connectionType;
        if (isCsvImport) displayName = `${connectionType} CSV Import`;

        return { id: connectionId, name: displayName, type: connectionType };
      })
      .filter((s): s is ReportSourceOption => s !== null);

    // Disambiguate duplicate names with the shop identifier from the connection id.
    const nameCounts: Record<string, number> = {};
    supportedConnections.forEach((s) => {
      nameCounts[s.name] = (nameCounts[s.name] || 0) + 1;
    });

    const disambiguated = supportedConnections.map((s) => {
      if (nameCounts[s.name] > 1) {
        const connId = s.id || '';
        const servicePrefix = s.type?.toLowerCase() || '';
        let suffix = connId.replace(new RegExp(`^${servicePrefix}_`, 'i'), '').replace(/_/g, ' ').trim();
        if (suffix.startsWith('csv-import-') || suffix.startsWith('csv import ')) {
          suffix = suffix.replace(/^csv[- ]import[- ]/, '').trim();
        }
        if (suffix && suffix !== connId) {
          return { ...s, name: `${s.name} (${suffix})` };
        }
        const dupes = supportedConnections.filter((d) => d.name === s.name);
        const idx = dupes.indexOf(s) + 1;
        return { ...s, name: `${s.name} (${idx})` };
      }
      return s;
    });

    sources.push(...disambiguated);

    if (supportedConnections.length > 1) {
      sources.unshift({ id: 'All', name: 'All Sources', type: 'All' });
    }
  }

  return sources.length > 0 ? sources : [{ id: 'none', name: 'No supported connections', type: 'none' }];
}

/** Real (selectable) report sources, without the 'All' and 'none' entries. */
export const selectableReportSources = (options: ReportSourceOption[]): ReportSourceOption[] =>
  options.filter((source) => source.id !== 'All' && source.id !== 'none');

/** Smart default: prefer an API connection over a CSV import of the same platform. */
export function defaultReportSourceIds(options: ReportSourceOption[]): string[] {
  const filteredSources = selectableReportSources(options);
  const apiTypes = new Set(
    filteredSources.filter((source) => !source.name.includes('CSV Import')).map((source) => source.type),
  );
  return filteredSources
    .filter((source) => !(source.name.includes('CSV Import') && apiTypes.has(source.type)))
    .map((source) => source.id);
}

/** Label for a set of report source ids, as the report showed it. */
export function describeReportSourceIds(ids: string[], options: ReportSourceOption[]): string {
  const selectable = selectableReportSources(options);
  if (ids.length === 0) return 'No sources selected';
  if (selectable.length > 1 && ids.length === selectable.length) return 'All Sources';
  if (ids.length === 1) return selectable.find((s) => s.id === ids[0])?.name ?? ids[0];
  return `${ids.length} sources selected`;
}

export interface ResolvedReportSources {
  ids: string[];
  label: string;
  origin: ResolvedOrigin;
  /** Saved ids no longer connected and left out (FIG-409 · UC-233); 0 or absent otherwise. */
  missingCount?: number;
}

export function resolveReportSources(
  choice: ReportSourceChoiceLike | undefined,
  options: ReportSourceOption[],
): ResolvedReportSources {
  const selectable = selectableReportSources(options);
  if (choice?.kind === 'all' && selectable.length > 0) {
    const ids = selectable.map((s) => s.id);
    return { ids, label: describeReportSourceIds(ids, options), origin: 'saved' };
  }
  if (choice?.kind === 'source') {
    const match = selectable.find((s) => s.id === choice.id);
    if (match) return { ids: [match.id], label: match.name, origin: 'saved' };
  }
  if (choice?.kind === 'sources') {
    // Keep the saved ids still connected, in the report's order; fall back to
    // the default only when none are left (decided 22 Oct, WORK_PLAN §8 Q3).
    const ids = selectable.filter((s) => choice.ids.includes(s.id)).map((s) => s.id);
    if (ids.length > 0) {
      const missingCount = new Set(choice.ids).size - ids.length;
      // An explicit selection is never labelled "All Sources", even when it
      // matches every source still connected (Cursor Bugbot on #360; code-reviewer
      // agent finding 5): it stays explicit, so a reconnected source stays out.
      const label = ids.length > 1 ? `${ids.length} sources selected` : selectable.find((s) => s.id === ids[0])?.name ?? ids[0];
      return { ids, label, origin: 'saved', missingCount };
    }
    const defaultIds = defaultReportSourceIds(options);
    return { ids: defaultIds, label: describeReportSourceIds(defaultIds, options), origin: 'fallback', missingCount: new Set(choice.ids).size };
  }
  const ids = defaultReportSourceIds(options);
  return { ids, label: describeReportSourceIds(ids, options), origin: choice ? 'fallback' : 'default' };
}
